/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useDomainForm.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for managing domain form state and validation.
────────────────────────────────────────────────────────────────*/

import { useState, useMemo, useCallback } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import type { DomainFormState } from '../types';

type DomainFormFieldKey = keyof DomainFormState;

const FORM_ERROR_KEYS: DomainFormFieldKey[] = ['domainName', 'tenantId', 'language', 'timezone', 'countryCode'];

export function useDomainForm(
  formData: DomainFormState,
  originalData: DomainFormState,
  mode: 'create' | 'edit',
  setFormData: (data: DomainFormState | ((prev: DomainFormState) => DomainFormState)) => void
) {
  const { t } = useI18n();
  const [errors, setErrors] = useState<Partial<Record<DomainFormFieldKey, string>>>({});

  // Check if there are changes
  const hasChanges = useMemo(() => {
    if (mode === 'create') return true;
    return JSON.stringify(formData) !== JSON.stringify(originalData);
  }, [mode, formData, originalData]);

  // Validate form
  const validateForm = useCallback((): { isValid: boolean; errors: Partial<Record<DomainFormFieldKey, string>> } => {
    const newErrors: Partial<Record<DomainFormFieldKey, string>> = {};

    if (!formData.domainName.trim()) {
      newErrors.domainName = t('telephonyDomains.validation.domainNameRequired');
    }

    if (!formData.tenantId) {
      newErrors.tenantId = t('telephonyDomains.validation.tenantIdRequired');
    }

    if (!formData.language.trim()) {
      newErrors.language = t('telephonyDomains.validation.languageRequired');
    }

    if (!formData.timezone.trim()) {
      newErrors.timezone = t('telephonyDomains.validation.timezoneRequired');
    }

    if (!formData.countryCode.trim()) {
      newErrors.countryCode = t('telephonyDomains.validation.countryCodeRequired');
    }

    setErrors(newErrors);
    return { isValid: Object.keys(newErrors).length === 0, errors: newErrors };
  }, [formData, t]);

  // Handle input change
  const handleInputChange = useCallback(
    (field: keyof DomainFormState, value: DomainFormState[keyof DomainFormState]) => {
      const normalisedValue =
        (value === null ? undefined : value) as DomainFormState[keyof DomainFormState];
      setFormData(prev => ({
        ...prev,
        [field]: normalisedValue
      }));

      setErrors(prev => {
        if (!prev?.[field]) {
          return prev;
        }
        const { [field]: _removed, ...rest } = prev;
        return rest;
      });
    },
    [setFormData]
  );

  // Get input classes helper
  const inputClasses = useCallback(
    (hasError?: boolean) =>
      `w-full px-3 py-2 text-sm border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent ${
        hasError ? 'border-red-500' : 'border-border'
      }`,
    []
  );

  return {
    errors,
    hasChanges,
    validateForm,
    handleInputChange,
    inputClasses,
    FORM_ERROR_KEYS
  };
}


